// Layer 2 (Engine): session clock + perturbation source. Pure transitions;
// React provider lives in ScenarioProvider.tsx.

import type { Case } from '@/versions/v1/schema/case';
import type {
  Perturbation,
  ScenarioBundle,
  ScenarioId,
  ScenarioPhase,
  SessionState,
  WhatIfInputs,
} from '@/versions/v1/schema/scenario';
import { INITIAL_SESSION_STATE, WHAT_IF_DEFAULTS } from '@/versions/v1/schema/scenario';
import {
  CURSOR_BOUNDS,
  DEFAULT_CURSOR,
  type SeriesTime,
} from '@/versions/v1/schema/reading-series';
import { addMinutes, clamp, diffMinutes, formatClock } from '@/versions/v1/shared/clock.derive';
import type { AdvisorySessionState } from '@/versions/v1/shared/AdvisoryEngine';
import { diagnose } from '@/versions/v1/shared/DiagnosisEngine';
import {
  HARDNESS,
  perturbationsFor,
  SCENARIOS,
} from '@/versions/v1/shared/perturbations';

export { SCENARIOS, perturbationsFor };
export const INITIAL_STATE = INITIAL_SESSION_STATE;

export function applyCases(
  cases: readonly Case[],
  state: SessionState,
  advisory?: AdvisorySessionState,
): Case[] {
  const computed = diagnose(state, advisory).cases;
  return [...computed, ...cases];
}

function hasScenario(state: SessionState, id: ScenarioId): boolean {
  return state.scenarios.includes(id);
}

export function phaseFor(state: SessionState, advisory?: AdvisorySessionState): ScenarioPhase {
  return diagnose(state, advisory).phase;
}

function withHardnessOn(state: SessionState, cursor: SeriesTime): SessionState {
  const scenarios: ScenarioId[] = state.scenarios.includes('hardness-step-change')
    ? [...state.scenarios]
    : [...state.scenarios, 'hardness-step-change'];
  return { ...state, scenarios, whatIf: null, cursor: clamp(cursor, CURSOR_BOUNDS.min, CURSOR_BOUNDS.max) };
}

export function advance(state: SessionState): SessionState {
  const phase = phaseFor(state);
  if (phase === 'idle') {
    return withHardnessOn(state, HARDNESS.stops!.provisionalAt);
  }
  if (phase === 'provisional') {
    return withHardnessOn(state, HARDNESS.stops!.confirmsAt);
  }
  return reset(state);
}

export function reset(_state: SessionState): SessionState {
  return { cursor: DEFAULT_CURSOR, scenarios: [], whatIf: null, cadence: _state.cadence };
}

export function setPhase(state: SessionState, phase: ScenarioPhase): SessionState {
  if (phase === 'idle') return reset(state);
  if (phase === 'provisional') return withHardnessOn(state, HARDNESS.stops!.provisionalAt);
  return withHardnessOn(state, HARDNESS.stops!.confirmsAt);
}

export function stepCursor(state: SessionState, minutes: number): SessionState {
  const next = clamp(addMinutes(state.cursor, minutes), CURSOR_BOUNDS.min, CURSOR_BOUNDS.max);
  return { ...state, cursor: next };
}

export function setCursor(state: SessionState, at: SeriesTime): SessionState {
  return { ...state, cursor: clamp(at, CURSOR_BOUNDS.min, CURSOR_BOUNDS.max) };
}

export function toggleScenario(state: SessionState, id: ScenarioId): SessionState {
  const on = hasScenario(state, id);
  if (on) {
    return { ...state, scenarios: state.scenarios.filter((s) => s !== id) };
  }
  let next: SessionState = { ...state, scenarios: [...state.scenarios, id] as ScenarioId[], whatIf: null };
  if (id === 'hardness-step-change' && diffMinutes(next.cursor, HARDNESS.stops!.provisionalAt) < 0) {
    next = { ...next, cursor: HARDNESS.stops!.provisionalAt };
  }
  return next;
}

export function isWhatIfDefault(inputs: WhatIfInputs): boolean {
  return (
    inputs.feedStph === WHAT_IF_DEFAULTS.feedStph &&
    Math.abs(inputs.hardness - WHAT_IF_DEFAULTS.hardness) < 1e-9
  );
}

export function setWhatIf(state: SessionState, inputs: WhatIfInputs | null): SessionState {
  if (!inputs || isWhatIfDefault(inputs)) {
    return { ...state, whatIf: null };
  }
  let cursor = state.cursor;
  if (diffMinutes(cursor, HARDNESS.stops!.provisionalAt) < 0) {
    cursor = DEFAULT_CURSOR;
  }
  return { cursor, scenarios: [], whatIf: inputs, cadence: state.cadence };
}

export function clockLabel(state: SessionState): string {
  return formatClock(state.cursor);
}

export const SCENARIO_PHASE_LABEL: Record<ScenarioPhase, string> = {
  idle: 'Idle · design basis',
  provisional: 'Provisional · hardness flagged',
  confirmed: 'Confirmed · SA01 landed',
};

export const SCENARIO_ADVANCE_LABEL: Record<ScenarioPhase, string> = {
  idle: 'Play hardness step-change',
  provisional: 'Land SA01 assay',
  confirmed: 'Reset walkthrough',
};

export type { Perturbation, ScenarioBundle, ScenarioId, WhatIfInputs };

import type { PlantCadence } from '@/versions/v1/schema/unit-operation';

/** route-reads paul-call.A8 #256 -- switches the plant sample cadence; the
 * cursor, scenarios and what-if inputs are untouched (S2.1). */
export function setCadence(state: SessionState, cadence: PlantCadence): SessionState {
  if (state.cadence === cadence) return state;
  return { ...state, cadence };
}
